import { Bell, Clock, EllipsisVertical } from 'lucide-react';

import Menu from '@/components/Menu/Menu';
import AMP_KEYS from '@/configs/amp-track-keys';
import { useFeedbackTerminology } from '@/entities/workspace/hooks';
import { useStructuredStorageFilterQuery } from '@/features/StructuredFilter/hooks/useStructuredFilterQuery';
import StructuredFilter from '@/features/StructuredFilter/StructuredFilter';
import { Button, PageHeader, Skeleton, Tooltip } from '@/shared/ui';
import { DATE_FORMAT, formatTimeDiff, getTimezoneDayjs } from '@/utils/time';
import { track } from '@/utils/track/track';
import { formatNumber } from '@/utils/util';

import { useComputedIssueList, useGlobalSignalIssueState } from '../../hooks';
import { parseSignalDatePreset } from '../../lib';
import { useSignalLayout } from '../signals-layout';
import { SignalDutationSelecter } from './duration-selecter';

type Props = {
  lastUpdatedAt?: DateString;
};

export const SignalPageHeader = ({ lastUpdatedAt }: Props) => {
  return (
    <PageHeader
      title="Signals"
      desc="Detect emerging issues before they spread."
      topRightNode={<SignalHeaderActions lastUpdatedAt={lastUpdatedAt} />}
      bottomNode={<SignalHeaderBottom />}
    />
  );
};

const SignalHeaderActions = ({ lastUpdatedAt }: Props) => {
  const { openAlertPane, openHiddenIssuesPane, alertModalState } =
    useSignalLayout();

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-3">
      <LastUpdated lastUpdatedAt={lastUpdatedAt} />
      <Button size="sm" onClick={() => alertModalState.openCreate()}>
        <Bell className="size-3.5" />
        New alert
      </Button>
      <Menu
        triggerNode={
          <div>
            <Button size="icon" variant="ghost">
              <EllipsisVertical className="size-3.5" />
            </Button>
          </div>
        }
        options={[
          {
            label: 'Alerts',
            onClick: openAlertPane,
          },
          {
            label: 'Hidden issues',
            onClick: openHiddenIssuesPane,
          },
        ]}
      />
    </div>
  );
};

const SignalHeaderBottom = () => {
  const { filterQueryFormats, onFilterSetChange, clearFilter } =
    useStructuredStorageFilterQuery('signals-dynamic-filter');
  const { dateSelection, setDateSelection } = useGlobalSignalIssueState();
  const { replaceFeedbackTerminology } = useFeedbackTerminology();

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 pt-4">
      <StructuredFilter
        values={filterQueryFormats}
        onChange={onFilterSetChange}
        clearFilter={clearFilter}
      />
      <div className="flex items-center gap-4">
        <FeedbackCount
          replaceFeedbackTerminology={replaceFeedbackTerminology}
        />
        <SignalDutationSelecter
          value={dateSelection}
          onChange={(selection) => {
            void track(AMP_KEYS.SIGNAL_DATE_PRESET_CHANGE, {
              date_preset:
                selection.type === 'preset' ? selection.preset : 'custom',
              date_range_days: parseSignalDatePreset(selection).value,
            });
            setDateSelection(selection);
          }}
        />
      </div>
    </div>
  );
};

const FeedbackCount = (props: {
  replaceFeedbackTerminology: (text: string) => string;
}) => {
  const { computedIssues, isLoading } = useComputedIssueList();
  const count = getFeedbackCountFromIssueList(computedIssues);

  if (isLoading) {
    return <Skeleton className="h-4 w-20" />;
  }

  return (
    <div className="text-xs text-dune-500">
      {formatNumber(count)} {props.replaceFeedbackTerminology('feedbacks')}
    </div>
  );
};

export const getFeedbackCountFromIssueList = (
  issues: Array<{ selectedPeriodCount: number }>,
) =>
  issues.reduce((acc, issue) => acc + issue.selectedPeriodCount, 0);

const LastUpdated = ({ lastUpdatedAt }: Props) => {
  if (!lastUpdatedAt) {
    return null;
  }

  const displayText =
    formatTimeDiff(lastUpdatedAt) === 'just now'
      ? 'Updated just now'
      : `Updated ${formatTimeDiff(lastUpdatedAt)} ago`;

  return (
    <Tooltip
      content={
        <span>
          Updated at{' '}
          {getTimezoneDayjs(lastUpdatedAt).format(DATE_FORMAT.readable)}
        </span>
      }
    >
      <div className="flex items-center gap-0.5 text-dune-500">
        <Clock className="size-3" />
        <span className="text-xs">{displayText}</span>
      </div>
    </Tooltip>
  );
};
